/* eslint-disable react-hooks/exhaustive-deps */
/**
 * DuplicatesMode — Phase 1.4.2 near-duplicate triage for the brand pool.
 *
 * Why this exists: the user uploaded a screenshot of 4 near-identical
 * Remi portraits and asked "flag these and let me pick the best one,
 * drop the rest". Stock assets already carry pHash; native SharePoint
 * assets did not. Backend now computes pHash on ingest + via a
 * resumable backfill script.
 *
 * Page surface:
 *   - One row per duplicate group (≥2 assets, Hamming distance ≤ threshold)
 *   - Side-by-side thumbnails per group, suggested "best" pre-selected
 *     (highest classifier verdict, largest file, most recent activity)
 *   - Operator clicks a different asset to flip the "best" choice
 *   - Resolve button: soft-deletes losers, keeps the chosen one active
 *   - Skip / dismiss removes the group from this session without writing
 *
 * Backend endpoints reused:
 *   GET  /api/v2/workspaces/{ws}/brand/duplicates?threshold=8
 *   POST /api/v2/workspaces/{ws}/brand/duplicates/resolve
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Slider } from "../components/ui/slider";
import {
  ArrowLeft,
  Check,
  X as XIcon,
  RotateCw,
  ImageOff,
  Trophy,
  Sparkles,
  Loader2,
  AlertTriangle,
  SkipForward,
} from "lucide-react";
import { toast } from "sonner";
import { API, authedFetch, verdictMeta } from "../components/gallery/utils";

const DEFAULT_WS = "a24c8722-c708-4511-a5cc-8b98a884bbfb";

function fmtBytes(n) {
  if (!n) return "?";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function shortFolder(path) {
  if (!path) return "(unknown folder)";
  const parts = path.split("/").filter(Boolean);
  return parts.slice(-2).join(" / ") || path;
}

function GroupCard({ group, onResolve, onSkip, resolving }) {
  const [chosenKeep, setChosenKeep] = useState(group.suggested_keep_asset_id);
  const keeperMember = useMemo(
    () => group.members.find((m) => m.asset_id === chosenKeep),
    [group, chosenKeep],
  );
  const dropIds = group.members
    .filter((m) => m.asset_id !== chosenKeep)
    .map((m) => m.asset_id);

  const handleResolve = () => {
    onResolve({ group_id: group.group_id, keep_asset_id: chosenKeep, drop_asset_ids: dropIds });
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 space-y-3"
      data-testid={`duplicates-group-${group.group_id}`}
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900">
              {group.size} near-duplicate photos
            </h3>
            <Badge variant="outline" className="text-[10px]">
              max distance {group.max_distance}
            </Badge>
            {group.max_distance === 0 ? (
              <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">
                identical hash
              </Badge>
            ) : null}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Pick the photo to keep — the other {group.size - 1} will be moved out of the active pool.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onSkip(group.group_id)}
            data-testid={`duplicates-skip-${group.group_id}`}
          >
            <SkipForward className="h-3.5 w-3.5 mr-1" /> Skip
          </Button>
          <Button
            size="sm"
            onClick={handleResolve}
            disabled={resolving || dropIds.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`duplicates-resolve-${group.group_id}`}
          >
            {resolving ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Resolving…</>
            ) : (
              <><Check className="h-3.5 w-3.5 mr-1" /> Keep 1 · Drop {dropIds.length}</>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {group.members.map((m) => {
          const isKeep = m.asset_id === chosenKeep;
          const isSuggested = m.asset_id === group.suggested_keep_asset_id;
          const verdictM = verdictMeta(m.classifier_verdict);
          return (
            <button
              type="button"
              key={m.asset_id}
              onClick={() => setChosenKeep(m.asset_id)}
              className={`relative text-left rounded-lg border-2 overflow-hidden transition ${
                isKeep
                  ? "border-emerald-500 ring-2 ring-emerald-200"
                  : "border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`duplicates-member-${m.asset_id}`}
            >
              <div className="aspect-square bg-slate-100 relative overflow-hidden">
                {m.thumbnail_url ? (
                  <img
                    src={m.thumbnail_url}
                    alt={m.file_name || m.asset_id}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <ImageOff className="h-8 w-8" />
                  </div>
                )}
                {isKeep ? (
                  <div className="absolute top-2 left-2 bg-emerald-500 text-white rounded-full p-1.5 shadow-md">
                    <Trophy className="h-3.5 w-3.5" />
                  </div>
                ) : (
                  <div className="absolute top-2 left-2 bg-white/90 text-rose-700 rounded-full p-1.5 shadow-md">
                    <XIcon className="h-3.5 w-3.5" />
                  </div>
                )}
                {isSuggested && !isKeep ? (
                  <div className="absolute top-2 right-2 bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 text-[10px] font-medium border border-amber-200">
                    suggested
                  </div>
                ) : null}
              </div>
              <div className="p-2 space-y-1 bg-white">
                <div className="text-xs font-medium text-slate-800 truncate" title={m.file_name}>
                  {m.file_name || "(no name)"}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap text-[10px] text-slate-500">
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${verdictM.color}`} />
                  <span className={verdictM.textColor}>{verdictM.label}</span>
                  <span>· {fmtBytes(m.size_bytes)}</span>
                </div>
                <div className="text-[10px] text-slate-400 truncate" title={m.source_folder}>
                  {shortFolder(m.source_folder)}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="text-[11px] text-slate-500">
        Keeper: <span className="font-mono">{(keeperMember?.file_name) || chosenKeep.slice(0, 12)}</span>
      </div>
    </div>
  );
}

export default function DuplicatesMode({ user }) {
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get("ws") || DEFAULT_WS;
  const [threshold, setThreshold] = useState(8);
  // Debounced threshold — slider commits to this after 300ms idle so the
  // user can drag freely without firing one request per pixel tick.
  const [committedThreshold, setCommittedThreshold] = useState(8);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const [resolvedIds, setResolvedIds] = useState(() => new Set());
  const [skippedIds, setSkippedIds] = useState(() => new Set());
  const [stats, setStats] = useState({ kept: 0, dropped: 0, skipped: 0 });

  // 300ms debounce: slider movements coalesce before triggering a fetch.
  useEffect(() => {
    const t = setTimeout(() => setCommittedThreshold(threshold), 300);
    return () => clearTimeout(t);
  }, [threshold]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/duplicates?threshold=${committedThreshold}&active_only=true`,
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setData(json);
    } catch (e) {
      setError(e.message || "Could not load duplicates");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, committedThreshold]);

  useEffect(() => { load(); }, [load]);

  const handleResolve = async ({ group_id, keep_asset_id, drop_asset_ids }) => {
    setResolvingId(group_id);
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/duplicates/resolve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            keep_asset_id,
            drop_asset_ids,
            reason: "operator-selected best of duplicate group via DuplicatesMode",
          }),
        },
      );
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status}: ${t.slice(0, 200)}`);
      }
      const json = await r.json();
      setResolvedIds((prev) => {
        const next = new Set(prev);
        next.add(group_id);
        return next;
      });
      setStats((s) => ({
        ...s,
        kept: s.kept + 1,
        dropped: s.dropped + (json.dropped || []).length,
      }));
      toast.success(`Kept 1, dropped ${(json.dropped || []).length}`);
    } catch (e) {
      toast.error(e.message || "Could not resolve");
    } finally {
      setResolvingId(null);
    }
  };

  const handleSkip = (group_id) => {
    setSkippedIds((prev) => {
      const next = new Set(prev);
      next.add(group_id);
      return next;
    });
    setStats((s) => ({ ...s, skipped: s.skipped + 1 }));
  };

  const visibleGroups = useMemo(() => {
    if (!data || !data.groups) return [];
    return data.groups.filter(
      (g) => !resolvedIds.has(g.group_id) && !skippedIds.has(g.group_id),
    );
  }, [data, resolvedIds, skippedIds]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="duplicates-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <Link
              to="/admin/brand-assets"
              className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-1"
              data-testid="duplicates-back-link"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to gallery
            </Link>
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-600" /> Near-duplicate photos
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              Pick the best photo from each group and drop the rest. Dropped photos stay in the database with an audit log entry — nothing is hard-deleted.
            </p>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="text-right">
              <div className="text-slate-500">Session</div>
              <div className="font-mono text-slate-800" data-testid="duplicates-session-counter">
                ✓ {stats.kept} kept · ✗ {stats.dropped} dropped · → {stats.skipped} skipped
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="duplicates-refresh">
              {loading && data ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Refreshing</>
              ) : (
                <><RotateCw className="h-3.5 w-3.5 mr-1" /> Refresh</>
              )}
            </Button>
          </div>
        </div>

        {/* Threshold control */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="min-w-0">
              <label className="text-xs font-medium text-slate-700">
                Sensitivity (Hamming distance ≤ <span className="font-mono">{threshold}</span>)
              </label>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Lower = stricter (only near-identical). Higher = looser (catches similar scenes).
              </p>
            </div>
            <div className="flex-1 min-w-[200px] max-w-md">
              <Slider
                value={[threshold]}
                onValueChange={(v) => setThreshold(v[0] || 8)}
                min={2}
                max={16}
                step={1}
                data-testid="duplicates-threshold-slider"
              />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                <span>2 strict</span>
                <span>8 default</span>
                <span>16 loose</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats banner */}
        {data && !loading && !error ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div className="bg-white rounded border border-slate-200 p-3" data-testid="duplicates-stat-groups">
              <div className="text-slate-500">Duplicate groups</div>
              <div className="text-2xl font-semibold text-slate-900">{data.group_count}</div>
            </div>
            <div className="bg-white rounded border border-slate-200 p-3" data-testid="duplicates-stat-photos">
              <div className="text-slate-500">Photos in groups</div>
              <div className="text-2xl font-semibold text-slate-900">{data.duplicate_asset_count}</div>
            </div>
            <div className="bg-white rounded border border-slate-200 p-3" data-testid="duplicates-stat-hashed">
              <div className="text-slate-500">Active photos hashed</div>
              <div className="text-2xl font-semibold text-slate-900">{data.total_assets_with_phash}</div>
            </div>
            <div className="bg-white rounded border border-slate-200 p-3" data-testid="duplicates-stat-pending">
              <div className="text-slate-500">Backfill pending</div>
              <div className="text-2xl font-semibold text-slate-900">{data.total_assets_missing_phash}</div>
            </div>
          </div>
        ) : null}

        {data && (data.total_assets_missing_phash || 0) > 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2 text-xs" data-testid="duplicates-backfill-warning">
            <AlertTriangle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-amber-900">
              <strong>{data.total_assets_missing_phash}</strong> active photos are still waiting for perceptual-hash backfill. They will appear here as additional duplicate groups once processed (re-run the script if needed: <code className="font-mono text-[11px] bg-amber-100 px-1 rounded">python scripts/backfill_phash.py --workspace {workspaceId}</code>).
            </div>
          </div>
        ) : null}

        {/* Body */}
        {loading && !data ? (
          <div className="space-y-3">
            <Skeleton className="h-32 w-full rounded-lg" data-testid="duplicates-loading" />
            <Skeleton className="h-32 w-full rounded-lg" />
          </div>
        ) : error ? (
          <div className="bg-rose-50 border border-rose-200 rounded p-4 text-sm text-rose-900" data-testid="duplicates-error">
            {error}
          </div>
        ) : visibleGroups.length === 0 ? (
          <div
            className="bg-white border border-slate-200 rounded-lg p-8 sm:p-12 text-center"
            data-testid="duplicates-empty"
          >
            <div className="inline-flex items-center justify-center bg-emerald-100 rounded-full p-4 mb-4">
              <Check className="h-6 w-6 text-emerald-700" />
            </div>
            <h2 className="text-base font-semibold text-slate-900">
              {data && data.group_count > 0 ? "Session complete" : "No duplicates found"}
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              {data && data.group_count > 0
                ? "You've worked through every duplicate group in this batch."
                : "No near-duplicate photos at this sensitivity. Try increasing the threshold."}
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={load} data-testid="duplicates-reload">
                <RotateCw className="h-3.5 w-3.5 mr-1" /> Reload queue
              </Button>
              <Link to="/admin/brand-assets">
                <Button size="sm" variant="ghost" data-testid="duplicates-back-to-gallery">
                  Back to gallery
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="duplicates-list">
            {visibleGroups.map((g) => (
              <GroupCard
                key={g.group_id}
                group={g}
                resolving={resolvingId === g.group_id}
                onResolve={handleResolve}
                onSkip={handleSkip}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
